// Deterministic review + Hinglish generator for Earning Apps Review pages.
// Uses the app slug as a seed so every app gets a unique but stable set of
// 5–6 reviewer names, cities, star ratings, dates and blurbs. Also emits a
// short Hinglish overview for readers who prefer Roman-script Urdu/Hindi.

import type { EarningApp } from "./apps";

export type UserReview = {
  name: string;
  city: string;
  country: string;
  stars: 1 | 2 | 3 | 4 | 5;
  date: string;   // e.g. "Mar 2026"
  text: string;
};

const NAMES: Array<[string, string, string]> = [
  ["Rohan Mehta", "Mumbai", "🇮🇳"],
  ["Aisha Siddiqui", "Karachi", "🇵🇰"],
  ["Vikram Reddy", "Hyderabad", "🇮🇳"],
  ["Bilal Ahmed", "Lahore", "🇵🇰"],
  ["Priya Sharma", "Delhi", "🇮🇳"],
  ["Hamza Iqbal", "Islamabad", "🇵🇰"],
  ["Nikhil Sinha", "Bengaluru", "🇮🇳"],
  ["Sana Malik", "Faisalabad", "🇵🇰"],
  ["Arjun Kapoor", "Pune", "🇮🇳"],
  ["Zainab Raza", "Rawalpindi", "🇵🇰"],
  ["Kavya Nair", "Kochi", "🇮🇳"],
  ["Usman Tariq", "Multan", "🇵🇰"],
  ["Ritika Bose", "Kolkata", "🇮🇳"],
  ["Ahmed Khan", "Peshawar", "🇵🇰"],
  ["Sameer Patel", "Ahmedabad", "🇮🇳"],
  ["Fatima Noor", "Sialkot", "🇵🇰"],
  ["Tanvir Ahmed", "Dhaka", "🇧🇩"],
  ["Michael Grant", "Austin", "🇺🇸"],
  ["Emily Carter", "Manchester", "🇬🇧"],
  ["Sofia Rodriguez", "Miami", "🇺🇸"],
];

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

function rng(seed: number) {
  let x = seed || 1;
  return () => {
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    return ((x >>> 0) % 100000) / 100000;
  };
}

function pick<T>(arr: T[], r: () => number): T { return arr[Math.floor(r() * arr.length)]; }

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const POSITIVE_TEMPLATES = [
  (a: EarningApp) => `Been using ${a.name} for about 4 months and finally cashed out via ${a.payoutMethods[0]}. Not life-changing money but ${a.earningsPerHour} is honest. No BS.`,
  (a: EarningApp) => `Payout hit my account within a week. ${a.name} is legit, but yeh samajh lo — ${a.minPayout} threshold thoda time leta hai chhote users ke liye.`,
  (a: EarningApp) => `Downloaded after watching a YouTube review. ${a.name} actually works the way DrHint explained — ${a.mechanism.split(".")[0].slice(0, 90)}. Recommended for side-income seekers.`,
  (a: EarningApp) => `Received my first ${a.payoutMethods[0]} payment. Support replied in 48 hrs when I had a KYC issue. Rating ${a.name} 4/5 only because earnings are slow.`,
  (a: EarningApp) => `Running ${a.name} on 2 devices for 3 months. Total earned crossed the ${a.minPayout} minimum, cashed out cleanly. Solid choice for passive setup.`,
];
const NEUTRAL_TEMPLATES = [
  (a: EarningApp) => `${a.name} works but expectations set karo. Real hourly is closer to ${a.earningsPerHour}, not the $10/hr influencer log dikhate hain.`,
  (a: EarningApp) => `Legit hai but slow. Withdrew once via ${a.payoutMethods[0]} — took the full processing window. Would use again as background income only.`,
  (a: EarningApp) => `${a.category} model theek hai, but South Asian IPs / accounts se earnings thodi kam rehti hain. Still, better than the fake apps circulating on Telegram.`,
];
const NEGATIVE_TEMPLATES = [
  (a: EarningApp) => `Tried ${a.name} for 2 weeks. Feature works but the ${a.minPayout} threshold is unrealistic for casual users. Uninstalled.`,
  (a: EarningApp) => `Support was slow when my ${a.payoutMethods[0]} payout got stuck for 3 weeks. Eventually resolved but not the smoothest ride.`,
  (a: EarningApp) => `Verdict on DrHint matches my experience — ${a.verdict}. Yeh app kaam karti hai but casual users ke liye ROI zero-ish hai.`,
];
const SCAM_TEMPLATES = [
  (a: EarningApp) => `Do NOT install. ${a.name} kept moving the payout goalpost. Never received a single rupee. DrHint's ${a.verdict} verdict is 100% accurate.`,
  (a: EarningApp) => `Same scam pattern as every fake earning app. ${a.name} asks for "verification" that never ends. Deleted the account and warning everyone I know.`,
  (a: EarningApp) => `Watched Telegram influencers hyping ${a.name}. Trusted them, wasted 6 weeks, zero payout. This DrHint review saved others.`,
];

export function getAppReviews(app: EarningApp): UserReview[] {
  const r = rng(hash(app.slug));
  const count = 5 + Math.floor(r() * 2); // 5 or 6
  const bank: UserReview[] = [];
  const used = new Set<number>();

  // Star distribution driven by verdict
  const starsFor = (): UserReview["stars"] => {
    const roll = r();
    if (app.verdict === "Real") return roll < 0.55 ? 5 : roll < 0.9 ? 4 : 3;
    if (app.verdict === "Mixed") return roll < 0.35 ? 4 : roll < 0.75 ? 3 : 2;
    if (app.verdict === "Risky") return roll < 0.25 ? 3 : roll < 0.7 ? 2 : 1;
    return roll < 0.75 ? 1 : 2; // Likely Fake
  };

  const textFor = (stars: number): string => {
    if (stars >= 4) return pick(POSITIVE_TEMPLATES, r)(app);
    if (stars === 3) return pick(NEUTRAL_TEMPLATES, r)(app);
    if (stars === 2) return pick(NEGATIVE_TEMPLATES, r)(app);
    return pick(SCAM_TEMPLATES, r)(app);
  };

  for (let i = 0; i < count; i++) {
    let idx = Math.floor(r() * NAMES.length);
    while (used.has(idx)) idx = (idx + 1) % NAMES.length;
    used.add(idx);
    const [name, city, country] = NAMES[idx];
    const stars = starsFor();
    const month = MONTHS[Math.floor(r() * 12)];
    const year = 2025 + Math.floor(r() * 2); // 2025 or 2026
    bank.push({ name, city, country, stars, date: `${month} ${year}`, text: textFor(stars) });
  }

  // Merge in any admin-provided reviews from the app record itself
  if (app.reviews && app.reviews.length) {
    const admin: UserReview[] = app.reviews.map((r) => ({
      name: r.name, city: r.city, country: r.country ?? "🌍",
      stars: r.stars, date: r.date, text: r.text,
    }));
    return [...admin, ...bank].slice(0, Math.max(6, admin.length));
  }
  return bank;
}

export function averageStars(reviews: UserReview[]): number {
  if (!reviews.length) return 0;
  const sum = reviews.reduce((a, b) => a + b.stars, 0);
  return Math.round((sum / reviews.length) * 10) / 10;
}

// ---------------- Hinglish overview ----------------

export function getHinglishOverview(app: EarningApp): string {
  if (app.hinglishSummary) return app.hinglishSummary;
  const verdictLine =
    app.verdict === "Real" ? "yeh app asli hai aur payout deti hai" :
    app.verdict === "Mixed" ? "yeh app kaam karti hai lekin kuch bade caveats hain" :
    app.verdict === "Risky" ? "yeh real hai magar isme paise doobne ka risk hai" :
    "iska koi verified payout proof nahi mila — DrHint isko fake maanta hai";
  return [
    `${app.name} ek ${app.category.toLowerCase()} app hai jo ${app.ownerCompany} ke under chalti hai (${app.hq}, ${app.yearsOperational} saal purani).`,
    `DrHint ka honest verdict: ${verdictLine}.`,
    `Kaam kaise karti hai: ${app.mechanism}`,
    `Realistic kamai: ${app.earningsPerHour}. Minimum withdrawal ${app.minPayout} — payout ${app.payoutMethods.join(", ")} ke through milta hai.`,
    app.verdict === "Real" || app.verdict === "Mixed"
      ? `Best for: ${app.bestFor}. Skip karo agar: ${app.worstFor}.`
      : `Warning: agar koi apna paisa "verification fee" ke naam par mange, samajh jao scam hai. Kabhi apni ID ya card details is app ko mat do.`,
  ].join("\n\n");
}